import React, { useMemo } from "react";
import { Table, Spin, Typography, Button, Tooltip as AntTooltip, Image as AntImage } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { ALL_COLUMNS, formatBool } from "../../../../util/helper";
import { Link, useParams } from "react-router-dom";

const { Text } = Typography;

const HEADER_BG = { molprops: "#f6ffed", glide: "#f9f0ff", phase: "#fff0f6", adme: "#fff7e6" };
const WrappedHeader = ({ title }) => <div style={{ lineHeight: "1.2", textAlign: "center" }}>{title}</div>;

export const MoleculeTable = ({
  loading,
  pagedData,
  selectedMap,
  onSelectionChange,
  visibleColumns,
  images,
  dynamicColumnGroups,
  handleCopyToClipboard,
  onSelectAll,
  sortConfig,
  onSortChange,
}) => {
  const {
    dynamicGlideColumns,
    dynamicPhaseColumns,
    dynamicAdmeColumns,
    dynamicMolProps,
  } = dynamicColumnGroups;

  const { projectId, batchId } = useParams();

  const tableColumns = useMemo(() => {
    const getSortProps = (columnKey) => ({
      sorter: true,
      sortOrder: sortConfig?.columnKey === columnKey ? sortConfig.order : null,
      sortDirections: ["ascend", "descend", "ascend"],
    });

    const columns = [];

    ALL_COLUMNS.forEach((cfg) => {
      if (!visibleColumns.includes(cfg.key) && !cfg.isGroup) return;

      if (cfg.isGroup) {
        const groupKey = cfg.key;
        const dynamicSubColumns =
          groupKey === "glide"
            ? dynamicGlideColumns
            : groupKey === "phase"
              ? dynamicPhaseColumns
              : groupKey === "adme"
                ? dynamicAdmeColumns
                : dynamicMolProps;
        const visibleSubColumns = dynamicSubColumns.filter((subCol) => visibleColumns.includes(subCol.key));
        if (!visibleSubColumns.length || !visibleColumns.includes(groupKey)) return;

        columns.push({
          title: <WrappedHeader title={cfg.title} />,
          onHeaderCell: () => ({ style: { background: HEADER_BG[groupKey] } }),
          children: visibleSubColumns.map((subCol) => {
            if (groupKey === "glide" || groupKey === "phase") {
              return {
                title: <WrappedHeader title={`${subCol.file} (pH=${subCol.ph_threshold})`} />,
                key: subCol.key,
                width: 140,
                align: "center",
                ...getSortProps(subCol.key),
                render: (_unused, record) => {
                  const match = (record[groupKey] || []).find(
                    (item) => item.file === subCol.file && item.ph_threshold === subCol.ph_threshold
                  );
                  if (!match) return "N/A";
                  const textValue =
                    groupKey === "glide"
                      ? (isFinite(Number(match.score)) ? Number(match.score).toFixed(2) : String(match.score ?? "N/A"))
                      : (match.matched_ligand_sites || "N/A");
                  const recordId = match.glide_id || match.phase_id;
                  const pageType = groupKey === "glide" ? "glide-results" : "phase-results";
                  return recordId ? (
                    <Link
                      to={`/project/${projectId}/batch/${batchId}/${pageType}/${recordId}`}
                      state={{ prefillMoleculeId: record.molecule_id }}
                    >
                      {textValue}
                    </Link>
                  ) : (
                    textValue
                  );
                },
              };
            }

            if (groupKey === "adme") {
              return {
                title: <WrappedHeader title={`${subCol.parent}(${subCol.child})`} />,
                key: subCol.key,
                width: 160,
                align: "center",
                onHeaderCell: () => ({ style: { background: HEADER_BG.adme } }),
                ...getSortProps(subCol.key),
                render: (_unused, record) => {
                  const admeObject = record?.[subCol.fullKey];
                  const value = admeObject?.value;
                  const admeId = admeObject?.adme_id;
                  const displayValue =
                    value === undefined || value === null || value === ""
                      ? "N/A"
                      : (typeof value === "boolean" ? formatBool(value) : String(value));
                  return admeId ? (
                    <Link
                      to={`/project/${projectId}/batch/${batchId}/adme-results/${admeId}`}
                      state={{ prefillMoleculeId: record.molecule_id }}
                    >
                      {displayValue}
                    </Link>
                  ) : (
                    displayValue
                  );
                },
              };
            }

            return {
              title: <WrappedHeader title={subCol.title} />,
              key: subCol.key,
              width: subCol.width || 100,
              align: "center",
              onHeaderCell: () => ({ style: { background: HEADER_BG.molprops } }),
              ...getSortProps(subCol.key),
              render: (_unused, record) => {
                const value = record?.[subCol.sourceKey];
                if (value === undefined || value === null || value === "") return "N/A";
                if (typeof value === "number") return value;
                return String(value);
              },
            };
          }),
        });
        return;
      }

      const baseProps = {
        title: <WrappedHeader title={cfg.title} />,
        dataIndex: cfg.key,
        key: cfg.key,
        width: cfg.width,
        align: cfg.align || "center",
      };

      if (cfg.key === "structure_2d") {
        columns.push({
          ...baseProps,
          width: 220,
          render: (_unused, record) => {
            const imageUrl = images[record.molecule_id];
            return (
              <div style={{ position: "relative", height: 150, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {imageUrl ? (
                  <AntImage
                    src={imageUrl}
                    width={150}
                    height={150}
                    style={{ objectFit: "contain" }}
                    preview={{ mask: "View" }}
                  />
                ) : (
                  <Spin size="small" />
                )}
                <AntTooltip title="Copy SMILES">
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => handleCopyToClipboard(record.smile)}
                    style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)" }}
                  />
                </AntTooltip>
              </div>
            );
          },
        });
        return;
      }

      if (cfg.key === "smile") {
        return;
      }

      if (cfg.key === "molecule_id") {
        columns.push({
          ...baseProps,
          render: (value) => <Text ellipsis>{value ?? "N/A"}</Text>,
        });
        return;
      }

      if (cfg.key === "reward") {
        columns.push({
          ...baseProps,
          ...getSortProps(cfg.key),
          align: "right",
          render: (value) => (typeof value === "number" ? value.toFixed(3) : "N/A"),
        });
        return;
      }

      columns.push({
        ...baseProps,
        ...getSortProps(cfg.key),
        render: (value) => <Text ellipsis>{value ?? "N/A"}</Text>,
      });
    });

    return columns;
  }, [
    visibleColumns,
    images,
    dynamicGlideColumns,
    dynamicPhaseColumns,
    dynamicAdmeColumns,
    dynamicMolProps,
    handleCopyToClipboard,
    projectId,
    batchId,
    sortConfig,
  ]);

  return (
    <Spin spinning={loading}>
      <Table
        rowSelection={{
          selectedRowKeys: Object.keys(selectedMap),
          onSelect: (record, selected) => {
            const next = { ...selectedMap };
            if (selected) next[record.molecule_id] = record;
            else delete next[record.molecule_id];
            onSelectionChange(next);
          },
          onSelectAll: (checked) => {
            if (typeof onSelectAll === "function") {
              onSelectAll(checked);
            }
          },
        }}
        dataSource={pagedData}
        columns={tableColumns}
        bordered
        rowKey="molecule_id"
        pagination={false}
        size="small"
        scroll={{ x: "max-content" }}
        onChange={(_pagination, _filters, sorter) => {
          const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;
          if (!nextSorter || typeof onSortChange !== "function") return;

          onSortChange({
            columnKey: nextSorter.columnKey || nextSorter.field || nextSorter.column?.key || null,
            order: nextSorter.order || null,
          });
        }}
      />
    </Spin>
  );
};
